import React from 'react';
import { 
  Switch as MuiSwitch, 
  SwitchProps as MuiSwitchProps,
  FormControlLabel,
  FormControl,
  FormHelperText
} from '@mui/material';
import { StyledSwitch, StyledFormControl } from './Switch.style';

export interface SwitchProps extends Omit<MuiSwitchProps, 'onChange'> {
  /**
   * The label for the switch
   */
  label?: string;
  /**
   * Whether the switch is checked
   */
  checked?: boolean;
  /**
   * Callback fired when the switch is toggled
   */
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Whether the switch is disabled
   */
  disabled?: boolean;
  /**
   * Whether the field has an error
   */
  error?: boolean;
  /**
   * Helper text to display below the switch
   */
  helperText?: string;
  /**
   * The size of the switch
   */
  size?: 'small' | 'medium';
  /**
   * The color of the switch
   */
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  /**
   * Whether the switch is required
   */
  required?: boolean;
  /**
   * Position of the label relative to the switch
   */
  labelPlacement?: 'start' | 'end' | 'top' | 'bottom';
}

export const Switch: React.FC<SwitchProps> = ({
  label,
  checked = false,
  onChange,
  disabled = false,
  error = false,
  helperText,
  size = 'medium',
  color = 'primary',
  required = false,
  labelPlacement = 'end',
  ...props
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.checked, event);
    }
  };

  const switchElement = (
    <StyledSwitch
      checked={checked}
      onChange={handleChange}
      disabled={disabled}
      size={size}
      color={color}
      required={required}
      {...props}
    />
  );

  if (!label && !helperText) {
    return switchElement;
  }

  return (
    <StyledFormControl error={error} disabled={disabled}>
      {label ? (
        <FormControlLabel
          control={switchElement}
          label={label}
          labelPlacement={labelPlacement}
          disabled={disabled}
          required={required}
        />
      ) : (
        switchElement
      )}
      
      {helperText && (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
}; 